/**
 * GlobalDictionaryItemFormModal — panel ITE, „Globalne słowniki" — dodaj/edytuj pozycję.
 *
 * `code` jest niezmienialny po utworzeniu pozycji (matchowanie ze starymi rekordami
 * przez code — zmiana zerwie powiązania), więc w trybie edycji pole jest `disabled`.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/Button';
import { ColorPicker } from '@/components/configurable-table/ColorPicker';
import { FormField } from '@/components/FormField';
import { InfoHint } from '@/components/InfoHint';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import type { GlobalDictionaryItemFormValues } from '@/lib/global-dictionaries/api';

interface Props {
  open: boolean;
  mode: 'create' | 'edit';
  initialValues: GlobalDictionaryItemFormValues;
  saving?: boolean;
  serverErrors?: Record<string, string>;
  onSubmit: (values: GlobalDictionaryItemFormValues) => void;
  onClose: () => void;
}

export function emptyGlobalDictionaryItemFormValues(): GlobalDictionaryItemFormValues {
  return {
    code: '',
    label: '',
    sortOrder: '',
    color: '',
    isActive: true,
  };
}

export function GlobalDictionaryItemFormModal({
  open,
  mode,
  initialValues,
  saving,
  serverErrors,
  onSubmit,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const [code, setCode] = useState(initialValues.code);
  const [label, setLabel] = useState(initialValues.label);
  const [sortOrder, setSortOrder] = useState(initialValues.sortOrder);
  const [color, setColor] = useState(initialValues.color);
  const [isActive, setIsActive] = useState(initialValues.isActive);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      setCode(initialValues.code);
      setLabel(initialValues.label);
      setSortOrder(initialValues.sortOrder);
      setColor(initialValues.color);
      setIsActive(initialValues.isActive);
      setErrors({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (serverErrors) {
      setErrors((prev) => ({ ...prev, ...serverErrors }));
    }
  }, [serverErrors]);

  function validate(): boolean {
    const next: Record<string, string> = {};
    if (!code.trim()) {
      next.code = t('admin.globalDictionaries.itemForm.errors.codeRequired');
    } else if (code.trim().length > 100) {
      next.code = t('admin.globalDictionaries.itemForm.errors.codeLength');
    }
    if (!label.trim()) {
      next.label = t('admin.globalDictionaries.itemForm.errors.labelRequired');
    } else if (label.trim().length > 200) {
      next.label = t('admin.globalDictionaries.itemForm.errors.labelLength');
    }
    if (sortOrder !== '' && !/^-?\d+$/.test(sortOrder.trim())) {
      next.sortOrder = t('admin.globalDictionaries.itemForm.errors.sortOrderInvalid');
    }
    if (color.trim() && !/^#[0-9a-fA-F]{6}$/.test(color.trim())) {
      next.color = t('admin.globalDictionaries.itemForm.errors.colorInvalid');
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function handleSubmit() {
    if (!validate()) return;
    onSubmit({
      code: code.trim(),
      label: label.trim(),
      sortOrder: sortOrder.trim(),
      color: color.trim(),
      isActive,
    });
  }

  const titleKey =
    mode === 'create'
      ? 'admin.globalDictionaries.itemForm.titleCreate'
      : 'admin.globalDictionaries.itemForm.titleEdit';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t(titleKey)}
      size="sm"
      testId="global-dictionary-item-form-modal"
      // W trakcie zapisu tło i Esc nie zamykają okna („Anuluj" jest wyłączone; X w nagłówku zostaje).
      closeOnBackdrop={!saving}
      closeOnEsc={!saving}
      footer={
        <>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={saving}
            data-testid="global-dictionary-item-form-cancel"
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={saving}
            data-testid="global-dictionary-item-form-submit"
          >
            {mode === 'create' ? t('common.create') : t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <FormField label={t('admin.globalDictionaries.itemForm.code')} error={errors.code}>
          <Input
            value={code}
            onChange={(e) => {
              setCode(e.target.value);
              setErrors((prev) => ({ ...prev, code: '' }));
            }}
            disabled={mode === 'edit'}
            placeholder={t('admin.globalDictionaries.itemForm.codePlaceholder')}
            data-testid="global-dictionary-item-form-code"
          />
        </FormField>
        <FormField label={t('admin.globalDictionaries.itemForm.label')} error={errors.label}>
          <Input
            value={label}
            onChange={(e) => {
              setLabel(e.target.value);
              setErrors((prev) => ({ ...prev, label: '' }));
            }}
            placeholder={t('admin.globalDictionaries.itemForm.labelPlaceholder')}
            data-testid="global-dictionary-item-form-label"
          />
        </FormField>
        <FormField
          label={t('admin.globalDictionaries.itemForm.sortOrder')}
          error={errors.sortOrder}
        >
          <Input
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            placeholder={t('admin.globalDictionaries.itemForm.sortOrderPlaceholder')}
            data-testid="global-dictionary-item-form-sort-order"
          />
          <InfoHint>{t('common.sortOrderInfo')}</InfoHint>
        </FormField>
        <FormField label={t('admin.globalDictionaries.itemForm.color')} error={errors.color}>
          <ColorPicker
            value={color || undefined}
            onChange={(hex) => setColor(hex ?? '')}
            label={t('admin.globalDictionaries.itemForm.color')}
          />
        </FormField>
        <label className="flex items-center gap-2 text-sm text-text-secondary">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            data-testid="global-dictionary-item-form-is-active"
          />
          {t('admin.globalDictionaries.itemForm.isActive')}
        </label>
      </div>
    </Modal>
  );
}
